import { useEffect, useState } from 'react';

import {
  VALVES,
  getBindings,
  resetBindings,
  setBinding,
  subscribe,
  valveLabel,
} from '../../lib/keyBindings';
import Styles from './KeyBindings.module.css';

const keyText = (key) => (key === ' ' ? 'Space' : key.toUpperCase());

/**
 * "Keys" section of the Options screen: one row per valve of the chosen
 * instrument. Click a row, press the key you want.
 */
const KeyBindings = ({ instrument, showFourthValve = false }) => {
  const [, setVersion] = useState(0);
  const [capturing, setCapturing] = useState(null); // the valve waiting for a key
  const [message, setMessage] = useState('');

  useEffect(() => subscribe(() => setVersion((v) => v + 1)), []);

  useEffect(() => {
    setCapturing(null);
    setMessage('');
  }, [instrument]);

  // While waiting for a key, swallow every key press so nothing else (closing
  // the Options screen on Escape, the game itself) reacts to it.
  useEffect(() => {
    if (!capturing) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Tab') {
        setCapturing(null); // let focus move on
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (event.key === 'Escape') {
        setCapturing(null);
        setMessage('');
        return;
      }
      if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'AltGraph'].includes(event.key)) return;
      if (event.ctrlKey || event.metaKey || event.altKey) {
        setMessage('Pick a key without Ctrl, Alt or Cmd.');
        return;
      }
      const result = setBinding(instrument, capturing, event.key);
      if (!result.ok) {
        setMessage(result.message);
        return;
      }
      setMessage(
        result.swapped
          ? `That key was on ${valveLabel(instrument, result.swapped)}, so the two swapped.`
          : '',
      );
      setCapturing(null);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [capturing, instrument]);

  const bindings = getBindings(instrument);
  const valves = (VALVES[instrument] || []).filter((valve) => valve !== 'f' || showFourthValve);
  const changed = (VALVES[instrument] || []).some((valve) => bindings[valve] !== valve);

  return (
    <section className={Styles.section}>
      <div className={Styles.head}>
        <span className={Styles.label}>Keys</span>
        {changed && (
          <button
            type="button"
            className={Styles.reset}
            onClick={() => {
              resetBindings(instrument);
              setCapturing(null);
              setMessage('');
            }}
          >
            Reset
          </button>
        )}
      </div>

      <ul className={`${Styles.list} ${valves.length > 4 ? Styles.twoColumns : ''}`}>
        {valves.map((valve) => (
          <li key={valve} className={Styles.row}>
            <span className={Styles.name}>{valveLabel(instrument, valve)}</span>
            <button
              type="button"
              className={`${Styles.key} ${capturing === valve ? Styles.keyWaiting : ''}`}
              onClick={() => {
                setMessage('');
                setCapturing(capturing === valve ? null : valve);
              }}
              onBlur={() => setCapturing((now) => (now === valve ? null : now))}
              aria-label={`${valveLabel(instrument, valve)} key: ${keyText(bindings[valve])}. Click to change.`}
            >
              {capturing === valve ? 'Press a key' : keyText(bindings[valve])}
            </button>
          </li>
        ))}
      </ul>

      <p className={`${Styles.note} ${message ? Styles.noteShown : ''}`} aria-live="polite">
        {message || ' '}
      </p>
    </section>
  );
};

export default KeyBindings;